import type { VisitDto } from "@repo/data-store";
import { createContext, type ReactNode, useContext } from "react";

/** Shared visit permission, narrowed by record outcomes and finding prerequisites. */
export type ChartingState =
  /** Normal charting. */
  | { status: "editable" }
  /** The visit has not begun, or its encounter state is not loaded yet. */
  | { status: "draft" }
  /** Clinician marked the order Did Not Perform. */
  | { status: "notPerformed" }
  /** Finalized or abandoned exam, outside an amendment. */
  | { status: "readOnly" }
  /**
   * Editable in principle, but something upstream must happen first. `reason` is
   * shown verbatim — it carries today's hand-written messages ("Complete the
   * prerequisite finding first", "Complete the associated diagnostic procedure
   * before interpreting this finding").
   */
  | { status: "blocked"; reason: string };

const EDITABLE: ChartingState = { status: "editable" };

interface ChartingContext {
  state: ChartingState;
  visitState: ChartingState;
  canAmend: boolean;
}

const ChartingStateContext = createContext<ChartingContext>({
  state: EDITABLE,
  visitState: EDITABLE,
  canAmend: true,
});

/** Visit permissions are a ceiling; a nested editable field cannot reopen them. */
export function ChartingStateProvider({
  state,
  visit,
  purpose = "chart",
  children,
}: {
  state?: ChartingState;
  visit?: { encounterStatus: VisitDto["encounterStatus"] };
  /** Notes may outlive an outcome; exam amendments explicitly reopen finalized findings. */
  purpose?: "chart" | "note" | "amendment";
  children: ReactNode;
}): ReactNode {
  const parent = useContext(ChartingStateContext);
  if (visit) {
    // Check-in opens charting; virtual call admission is a separate concern.
    const status = visit.encounterStatus;
    const visitState: ChartingState =
      status === "planned" || status === undefined
        ? { status: "draft" }
        : status === "in-progress" || status === "arrived" || status === "triaged" || status === "onleave"
          ? EDITABLE
          : { status: "readOnly" };
    return (
      <ChartingStateContext.Provider
        value={{
          state: visitState,
          visitState,
          canAmend: visitState.status === "editable" || status === "finished",
        }}
      >
        {children}
      </ChartingStateContext.Provider>
    );
  }
  const local = state ?? EDITABLE;
  const inherited = purpose === "note" ? parent.visitState : parent.state;
  const effective =
    purpose === "amendment" && parent.canAmend ? local : inherited.status === "editable" ? local : inherited;
  return (
    <ChartingStateContext.Provider value={{ ...parent, state: effective }}>{children}</ChartingStateContext.Provider>
  );
}

export function useChartingState(): ChartingState {
  return useContext(ChartingStateContext).state;
}

/** Exams support an explicit amendment flow after the visit is finished. */
export function useCanAmendChart(): boolean {
  return useContext(ChartingStateContext).canAmend;
}

/** Shared gate for mutation slots, including header actions outside fieldsets. */
export function ChartingActions({
  children,
  amendment = false,
}: {
  children: ReactNode;
  amendment?: boolean;
}): ReactNode {
  const canChart = useCanChart();
  const canAmend = useCanAmendChart();
  return (amendment ? canAmend : canChart) ? children : null;
}

/** Whether fields in this subtree accept input. The predicate `<ChartField>` reads. */
export function useCanChart(): boolean {
  return useChartingState().status === "editable";
}

/**
 * The quiet inline hint for a `blocked` field — it sits next to the one field it
 * describes.
 *
 * Only `blocked` says anything. `draft` used to raise an amber "Charting
 * available when visit begins" banner on every card, which restated the
 * pre-visit banner the page already shows once, above all of them. The other
 * states are carried by the card header's status badge.
 */
export function ChartingStateNotice(): ReactNode {
  const state = useChartingState();
  if (state.status !== "blocked") return null;
  return <p className="text-xs text-muted-foreground">{state.reason}</p>;
}

/**
 * Wrapper for a card body that dims and disables itself when charting is closed.
 *
 * `fieldset[disabled]` is what actually blocks input — `useCanChart` only stops
 * controls rendering as editable, and a native disabled fieldset also keeps the
 * subtree out of the tab order.
 */
export function ChartingStateFieldset({ children }: { children: ReactNode }): ReactNode {
  const state = useChartingState();
  const editable = state.status === "editable";
  return (
    <fieldset disabled={!editable} className={editable ? "space-y-6" : "space-y-6 opacity-50"}>
      <ChartingStateNotice />
      {children}
    </fieldset>
  );
}

/** Patient records have their own lifecycle and are independent of the visit's charting ceiling. */
export function PatientChartingStateProvider({ children }: { children: ReactNode }): ReactNode {
  return (
    <ChartingStateContext.Provider value={{ state: EDITABLE, visitState: EDITABLE, canAmend: true }}>
      {children}
    </ChartingStateContext.Provider>
  );
}
